// =============================================================================
// api/followup.ts — typed client for the booking-followup-send edge function
// =============================================================================
// The automatic post-visit mails. Two of them, on two clocks:
//
//   'review'      — to everyone who visited. Two links, Google and
//                   Tripadvisor, each its own listing per venue, both routed
//                   through manage.neoseum.com so the domain in the mail is
//                   ours and so we can see which one they followed; a customer
//                   who clicks either is never asked again.
//   'suggestion'  — only when something is still undone at that venue,
//                   pitching exactly one experience.
//
// booking-campaign-send is the manual broadcast half; this is the per-visitor
// half. Same action-routed shape, same operator JWT + booking_marketing
// permission, same content model (per-locale variants, blocks or raw HTML).
//
// The one genuinely new idea here is `priority` on a suggestion template: it
// IS the suggestion order, deliberately separate from
// location_experience.sort_order (the shop's widget order), so marketing can
// change what gets upsold first without reshuffling the booking page.
import { BOOKING_API_URL, supabase } from '../lib/supabase';
import type { CampaignContent, CampaignLocale, RenderedPreview } from './marketing';

export type FollowupKind = 'review' | 'suggestion';

export interface FollowupSettings {
  // Two mails, two switches, two delays. Independent on purpose: the review
  // ask wants to land while the visit is fresh, the cross-sell wants room to
  // breathe, and turning one off must not touch the other. Both delays are
  // measured from the resolved END of the visit, not the booked slot time.
  review_enabled: boolean;
  review_delay_hours: number;
  suggestion_enabled: boolean;
  suggestion_delay_hours: number;
  // How long after check-in a visit counts as finished when the queue reports
  // no completion of its own. This is the branch that fires today.
  visit_timeout_hours: number;
  // Added to the last person's queue start when the queue reports starts but
  // no completions.
  run_allowance_minutes: number;
  // Stamped by the database when `enabled` goes false -> true. Visits that
  // ended before it are never followed up, so switching on cannot mail every
  // past visitor at once.
  active_from: string | null;
  max_age_hours: number;
  min_days_between_sends: number;
  per_tick: number;
  default_locale: CampaignLocale;
  // The platform marks shown on a review button, as {{google icon}} /
  // {{trip icon}}. One pair system-wide. PNG: AVIF and WebP do not render in
  // mail. null = no icon, and the button renders as text only.
  google_icon_url: string | null;
  tripadvisor_icon_url: string | null;
  updated_at: string;
}

export interface FollowupTemplate {
  id: string;
  kind: FollowupKind;
  // What a suggestion mail pitches. Always null on a review mail.
  suggest_experience_id: string | null;
  // null = every venue. A row naming a venue overrides the all-venue row for
  // the same experience.
  location_id: string | null;
  priority: number;
  content: CampaignContent;
  default_locale: CampaignLocale;
  enabled: boolean;
  created_at: string;
  updated_at: string;
  // Server-resolved display name of the suggested experience; null on a
  // review row.
  experience_name: string | null;
  locales: string[];
}

// One row of the dry run: what the next tick would do, without doing it.
export interface FollowupCandidate {
  departure_id: string;
  email: string;
  first_name: string | null;
  locale: string;
  location_name: string | null;
  visit_ended_at: string;
  has_google_review_url: boolean;
  has_tripadvisor_review_url: boolean;
  visited: string[];
  suggestion: string | null;
  // false = no template written for this suggestion yet, so the visit would be
  // recorded as skipped rather than mailed.
  would_send: boolean;
}

export interface FollowupStatRow {
  key: string;
  label: string;
  kind?: FollowupKind;
  location_id?: string | null;
  experience_id?: string;
  // Each venue is its own listing on each site; false means that venue's
  // review mail has nowhere to send people for that one.
  has_google_review_url?: boolean;
  has_tripadvisor_review_url?: boolean;
  sent: number;
  skipped: number;
  failed: number;
  delivered: number;
  opened: number;
  clicked: number;
  bounced: number;
  complained: number;
  // Followed a review link on our own domain. The one number that says the
  // review mail is working, split by which site they chose.
  review_clicks: number;
  review_clicks_google: number;
  review_clicks_tripadvisor: number;
}

export interface FollowupStats {
  by_mail: FollowupStatRow[];
  by_venue: FollowupStatRow[];
  // Grouped on what the customer actually DID, not what we pitched — the
  // answer to "which experience makes people leave a review".
  by_visited_experience: FollowupStatRow[];
}

export interface VenueMissingReviewLinks {
  id: string;
  name: string;
  // Which platforms this venue has no link for: 'google', 'tripadvisor'.
  missing: string[];
}

export interface FollowupError {
  error: string;
  message?: string;
}

export interface FollowupResult<T> {
  ok: boolean;
  data: T | null;
  error: FollowupError | null;
  status: number;
}

async function call<T>(
  action: string,
  body: Record<string, unknown> = {},
): Promise<FollowupResult<T>> {
  const { data: sessionData } = await supabase.auth.getSession();
  const jwt = sessionData.session?.access_token;
  if (!jwt) {
    return {
      ok: false,
      data: null,
      error: { error: 'unauthenticated', message: 'No active session' },
      status: 401,
    };
  }

  let res: Response;
  try {
    res = await fetch(`${BOOKING_API_URL}/booking-followup-send`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({ action, ...body }),
    });
  } catch (e) {
    return {
      ok: false,
      data: null,
      error: { error: 'network_error', message: e instanceof Error ? e.message : String(e) },
      status: 0,
    };
  }

  let json: unknown = null;
  try {
    json = await res.json();
  } catch {
    /* empty / non-JSON */
  }

  if (!res.ok) {
    return {
      ok: false,
      data: null,
      error: (json as FollowupError) ?? { error: 'unknown_error', message: `HTTP ${res.status}` },
      status: res.status,
    };
  }
  return { ok: true, data: json as T, status: res.status, error: null };
}

// due_counts is how many visits the NEXT tick would pick up per kind — the
// numbers to look at before flipping either switch for the first time.
export function getFollowupSettings() {
  return call<{
    settings: FollowupSettings;
    due_counts: Record<FollowupKind, number>;
    venues_missing_review_links: VenueMissingReviewLinks[];
    from_address: string;
  }>('get_settings');
}

export function saveFollowupSettings(settings: Partial<FollowupSettings>) {
  return call<{ ok: true; settings: FollowupSettings }>('save_settings', { settings });
}

// Returned review-first, then the suggestions in the order they are offered.
export function listFollowupTemplates() {
  return call<{ templates: FollowupTemplate[]; from_address: string }>('list_templates');
}

export function createFollowupTemplate(input: {
  kind: FollowupKind;
  // Required for kind 'suggestion', refused for kind 'review'.
  suggest_experience_id?: string | null;
  location_id?: string | null;
}) {
  return call<{ template_id: string }>('create_template', input);
}

export function saveFollowupTemplate(
  templateId: string,
  input: { content?: CampaignContent; default_locale?: CampaignLocale; enabled?: boolean },
) {
  return call<{ ok: true; template_id: string }>('save_template', {
    template_id: templateId,
    ...input,
  });
}

export function deleteFollowupTemplate(templateId: string) {
  return call<{ ok: true }>('delete_template', { template_id: templateId });
}

// THE suggestion order: array position becomes priority, same as the drag
// order in Setup > Experiences.
export function reorderFollowupTemplates(templateIds: string[]) {
  return call<{ ok: true; count: number }>('reorder_templates', { template_ids: templateIds });
}

// What the preview and the test mail report back about the links they used.
// The review links differ per venue and a missing one silently removes its
// button, so the editor says which venue it rendered against.
export interface ReviewLinkContext {
  venue: string | null;
  has_google_review_url: boolean;
  has_tripadvisor_review_url: boolean;
}

// Server-rendered example. suggestExperienceId fills the experience tokens
// with that experience's real copy, and locationId picks whose review links to
// show — both are the REAL values, so the preview reads like the real mail.
export function previewFollowup(
  content: CampaignContent,
  locale: CampaignLocale,
  defaultLocale: CampaignLocale,
  suggestExperienceId: string | null,
  locationId?: string | null,
) {
  return call<RenderedPreview & ReviewLinkContext>('preview', {
    content,
    locale,
    default_locale: defaultLocale,
    ...(suggestExperienceId ? { suggest_experience_id: suggestExperienceId } : {}),
    ...(locationId ? { location_id: locationId } : {}),
  });
}

// `to` empty = the logged-in operator's own address (server default).
export function testSendFollowup(
  templateId: string,
  locale: CampaignLocale,
  to?: string,
  plainSubject?: boolean,
  locationId?: string | null,
) {
  return call<
    { to: string; locale: string; sample_names: boolean; resend_id: string } & ReviewLinkContext
  >('test_send', {
    template_id: templateId,
    locale,
    ...(to ? { to } : {}),
    ...(plainSubject ? { plain_subject: true } : {}),
    ...(locationId ? { location_id: locationId } : {}),
  });
}

// The dry run, per kind. Read this before enabling: it is the only way to see
// that the logic matches what marketing expects, against real visits.
export function previewFollowupDue(kind: FollowupKind, limit = 25) {
  return call<{ kind: FollowupKind; candidates: FollowupCandidate[]; count: number }>(
    'due_preview',
    { kind, limit },
  );
}

export function followupStats() {
  return call<FollowupStats>('stats');
}
